import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Card,
  Col,
  DatePicker,
  Empty,
  PageHeader,
  Row,
  Spin,
  Tag,
  message,
  Tooltip,
  Divider,
} from "antd";
import { DownloadOutlined, FilePdfOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";

// PDF (pdfmake)
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import ButtonItem from "../../../Components/ButtonItem";
import SelectEmpleadosItem from "../../../Components/SelectEmpleadosItem";
import {
  construirHojaTabla,
  descargarLibro,
  limpiarNombreArchivo,
  texto,
  centro,
} from "../../../Components/ExcelTablaReporte";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

// Ajusta la ruta real donde tengas tu item
moment.locale("es");
const { RangePicker } = DatePicker;

class CalendarioEmpleado extends Component {
  state = {
    empleadoId: null,
    empleadoNombre: "",
    rango: [moment().startOf("month"), moment().endOf("month")],

    loading: false,
    calendario: [], // salida del backend (data)
    resumen: null,  // salida del backend (resumen)
  };

  componentDidMount() {
    // No consultamos hasta elegir empleado (para evitar “basura”)
  }

  // =========================
  // Helpers
  // =========================
  validarRangoMax120(rangoMoment) {
    if (!rangoMoment || !rangoMoment[0] || !rangoMoment[1]) return true;
    const dias = rangoMoment[1].startOf("day").diff(rangoMoment[0].startOf("day"), "days") + 1;
    return dias <= 120;
  }

  disabledDate = (current) => {
    const { rango } = this.state;
    const start = rango?.[0];
    const end = rango?.[1];

    if (start && !end) {
      const min = moment(start).subtract(119, "days").startOf("day");
      const max = moment(start).add(119, "days").endOf("day");
      return current && (current.isBefore(min) || current.isAfter(max));
    }

    if (!start && end) {
      const min = moment(end).subtract(119, "days").startOf("day");
      const max = moment(end).add(119, "days").endOf("day");
      return current && (current.isBefore(min) || current.isAfter(max));
    }

    return false;
  };

  // Convierte un día del backend a strings "bonitos"
  buildHorariosUI = (d) => {
    // Backend nuevo:
    // d.horariosTexto: "Horario A: 08:00 - 16:00 (+1) | ..."
    // d.descansosTexto: "Comida: 13:00 - 13:30 (30 min) | ..."
    // d.horarios: [{ nombre, rango, saltoDias, descansosTexto, ... }]
    if (!d?.tieneTurno) {
      return {
        horariosTexto: d?.horariosTexto || "Sin turno asignado",
        descansosTexto: d?.descansosTexto || "-",
        horariosDetalle: [],
      };
    }

    const horariosDetalle = Array.isArray(d.horarios) ? d.horarios : [];
    const horariosTexto =
      d.horariosTexto ||
      (horariosDetalle.length
        ? horariosDetalle.map((h) => `${h.nombre}: ${h.rango}`).join(" | ")
        : "Sin horario configurado");

    const descansosTexto =
      d.descansosTexto ||
      (horariosDetalle.length
        ? horariosDetalle.map((h) => h.descansosTexto).filter(Boolean).join(" | ")
        : "-");

    return { horariosTexto, descansosTexto, horariosDetalle };
  };

  // =========================
  // API
  // =========================
  async consultarCalendario() {
    const { empleadoId, rango } = this.state;

    if (!empleadoId) {
      this.setState({ calendario: [], resumen: null });
      return;
    }

    if (!this.validarRangoMax120(rango)) {
      message.warning("El rango no puede ser mayor a 120 días.");
      return;
    }

    this.setState({ loading: true });

    try {
      const res = await axios.post(
        "calendarioEmpleado/calendarioEmpleado",
        {
          empresaId: this.props.empresaId,
          empleadoId,
          // manda string ISO para que no haya broncas en backend
          fechaInicio: rango[0].format("YYYY-MM-DD"),
          fechaFin: rango[1].format("YYYY-MM-DD"),
        },
        { headers: this.props.headersToken }
      );

      if (res.data?.success !== 2) {
        this.setState({ calendario: [], resumen: null });
      } else {
        this.setState({
          calendario: res.data.data || [],
          resumen: res.data.resumen || null,
        });
      }
    } catch (err) {
      this.setState({ calendario: [], resumen: null });
      message.error("No se pudo cargar el calendario.");
    } finally {
      this.setState({ loading: false });
    }
  }

  // =========================
  // Events
  // =========================
  onChangeEmpleado = (value, option) => {
    // option puede variar según tu SelectEmpleadosItem.
    // Intentamos sacar el label/nombre de donde se pueda.
    const empleadoNombre =
      option?.label ||
      option?.children ||
      option?.nombre ||
      option?.title ||
      this.state.empleadoNombre ||
      "";

    this.setState(
      { empleadoId: value || null, empleadoNombre: empleadoNombre || "" },
      () => this.consultarCalendario()
    );
  };

  onChangeRango = (value) => {
    if (!value || !value[0] || !value[1]) {
      this.setState({ rango: value }, () => this.consultarCalendario());
      return;
    }

    if (!this.validarRangoMax120(value)) {
      message.warning("El rango no puede ser mayor a 120 días.");
      return;
    }

    this.setState({ rango: value }, () => this.consultarCalendario());
  };

  // =========================
  // EXPORT: Excel
  // =========================
  exportExcel = () => {
    const { calendario, empleadoNombre, rango } = this.state;

    if (!calendario?.length) {
      message.warning("No hay datos para exportar.");
      return;
    }

    const columnas = [
      { titulo: "Fecha", ancho: 16 },
      { titulo: "Día", ancho: 10 },
      { titulo: "Turno", ancho: 20 },
      { titulo: "Horarios", ancho: 40 },
      { titulo: "Descansos", ancho: 40 },
    ];

    const filas = calendario.map((d) => {
      const fecha = d?.fechaTexto || (d?.fecha ? moment(d.fecha).format("ddd DD/MM/YYYY") : "");
      const dia = d?.diaSemanaTexto || (d?.fecha ? moment(d.fecha).format("ddd") : "");
      const turno = d?.turnoNombre || (d?.tieneTurno ? "—" : "Sin turno");
      const { horariosTexto, descansosTexto } = this.buildHorariosUI(d);
      const enUnaLinea = (x) => (x ?? "").toString().replace(/\r?\n/g, " ");

      return [
        centro(fecha),
        centro(dia),
        texto(turno),
        texto(enUnaLinea(horariosTexto)),
        texto(enUnaLinea(descansosTexto)),
      ];
    });

    const descripcionFiltros = [
      `Empleado: ${empleadoNombre || "—"}`,
      `Del ${rango?.[0]?.format("DD/MM/YYYY")} al ${rango?.[1]?.format("DD/MM/YYYY")}`,
    ].join(" · ");

    descargarLibro(
      [
        {
          nombre: "Calendario",
          hoja: construirHojaTabla({ columnas, filas, totales: [] }, {
            titulo: "Calendario de Turnos por Empleado",
            empresaNombre: this.props.empresa,
            descripcionFiltros,
          }),
        },
      ],
      limpiarNombreArchivo(`calendario_${empleadoNombre || "empleado"}_${rango[0].format("YYYYMMDD")}_${rango[1].format("YYYYMMDD")}`)
    );
  };

  // =========================
  // EXPORT: PDF (pdfMake)
  // =========================
  exportPDF = () => {
    const { calendario, empleadoNombre, rango } = this.state;

    if (!calendario?.length) {
      message.warning("No hay datos para exportar.");
      return;
    }

    const body = [
      [
        { text: "Fecha", bold: true },
        { text: "Turno", bold: true },
        { text: "Horarios", bold: true },
        { text: "Descansos", bold: true },
      ],
    ];

    calendario.forEach((d) => {
      const fecha = d?.fechaTexto || (d?.fecha ? moment(d.fecha).format("ddd DD/MM/YYYY") : "");
      const turno = d?.turnoNombre || (d?.tieneTurno ? "—" : "Sin turno");
      const { horariosTexto, descansosTexto } = this.buildHorariosUI(d);

      body.push([fecha, turno, horariosTexto, descansosTexto]);
    });

    const docDefinition = {
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [30, 50, 30, 40],
      content: [
        { text: "Calendario de Turnos por Empleado", style: "title" },
        {
          columns: [
            {
              width: "*",
              text: [
                { text: "Empleado: ", bold: true },
                empleadoNombre || "—",
              ],
            },
            {
              width: "auto",
              text: [
                { text: "Rango: ", bold: true },
                `${rango?.[0]?.format("DD/MM/YYYY")} - ${rango?.[1]?.format("DD/MM/YYYY")}`,
              ],
            },
          ],
          margin: [0, 8, 0, 12],
        },
        {
          table: {
            headerRows: 1,
            widths: [90, 80, "*", 120],
            body,
          },
          layout: "lightHorizontalLines",
          fontSize: 9,
        },
        {
          text: `Generado: ${moment().format("DD/MM/YYYY HH:mm")}`,
          style: "footer",
          margin: [0, 10, 0, 0],
        },
      ],
      styles: {
        title: { fontSize: 14, bold: true },
        footer: { fontSize: 9, color: "#666666" },
      },
      defaultStyle: {
        fontSize: 10,
      },
    };

    pdfMake.createPdf(docDefinition).download(
      `calendario_${empleadoNombre || "empleado"}_${rango[0].format("YYYYMMDD")}_${rango[1].format("YYYYMMDD")}.pdf`
    );
  };

  // =========================
  // UI
  // =========================
  renderCalendario() {
    const { calendario } = this.state;

    if (!calendario?.length) {
      return (
        <div style={{ padding: 20 }}>
          <Empty description="Selecciona un empleado y un rango de fechas" />
        </div>
      );
    }

    return (
      <div style={{ maxHeight: "calc(100vh - 250px)", overflow: "auto", padding: 12 }}>
        {calendario.map((d, idx) => {
          const fechaTxt = d?.fechaTexto || (d?.fecha ? moment(d.fecha).format("ddd DD/MM/YYYY") : d?.fecha || "—");

          const { horariosTexto, descansosTexto, horariosDetalle } = this.buildHorariosUI(d);

          return (
            <Card
              key={`${d.fecha || d.fechaTexto || idx}`}
              size="small"
              style={{ marginBottom: 10 }}
              bodyStyle={{ padding: 12 }}
            >
              <Row gutter={[10, 6]} align="top">
                <Col flex="180px">
                  <div style={{ fontWeight: 600 }}>{fechaTxt}</div>
                  <div style={{ fontSize: 12, color: "#888" }}>
                    {d?.tieneTurno ? "Con turno" : "Sin turno"}
                  </div>
                </Col>

                <Col flex="auto">
                  <div style={{ fontWeight: 600 }}>
                    {d?.turnoNombre || "—"}
                  </div>

                  <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                    <span style={{ fontWeight: 600 }}>Horarios:</span>{" "}
                    {horariosTexto}
                  </div>

                  <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                    <span style={{ fontWeight: 600 }}>Descansos:</span>{" "}
                    {descansosTexto}
                  </div>

                  {/* Detalle opcional por horario */}
                  {horariosDetalle?.length ? (
                    <>
                      <Divider style={{ margin: "8px 0" }} />
                      <div style={{ fontSize: 12, color: "#555" }}>
                        {horariosDetalle.map((h) => (
                          <div key={h.horarioId} style={{ marginBottom: 6 }}>
                            <div style={{ fontWeight: 600 }}>
                              {h.nombre} — {h.rango}
                            </div>
                            {!!h.ventanaEntrada && (
                              <div>Ventana entrada: {h.ventanaEntrada}</div>
                            )}
                            {!!h.ventanaSalida && (
                              <div>Ventana salida: {h.ventanaSalida}</div>
                            )}
                            <div>
                              {h.descansosTexto || "-"}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : null}
                </Col>

                <Col>
                  {d?.tieneTurno ? (
                    <Tag color="green">Laboral</Tag>
                  ) : (
                    <Tag color="default">Sin turno</Tag>
                  )}
                </Col>
              </Row>
            </Card>
          );
        })}
      </div>
    );
  }

  render() {
    const { loading, rango, empleadoId, empleadoNombre, calendario } = this.state;

    return (
      <>
        <PageHeader
          title="Calendario de Turnos por Empleado"
          style={{
            border: "1px solid rgb(235, 237, 240)",
            backgroundColor: "white",
            marginBottom: 10,
          }}
          extra={
            <>
              <Tooltip title="Exportar a Excel">
                <ButtonItem
                  type="default"
                  icon={<DownloadOutlined />}
                  onClick={this.exportExcel}
                  disabled={!calendario?.length}
                >
                  Excel
                </ButtonItem>
              </Tooltip>

              <Tooltip title="Exportar a PDF">
                <ButtonItem
                  type="default"
                  icon={<FilePdfOutlined />}
                  onClick={this.exportPDF}
                  disabled={!calendario?.length}
                >
                  PDF
                </ButtonItem>
              </Tooltip>
            </>
          }
        />

        <Row gutter={10}>
          {/* Panel filtros */}
          <Col xs={24} md={8}>
            <Card
              title="Filtros"
              size="small"
              style={{
                minHeight: "80vh",
                borderRadius: 6,
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Empleado</div>

                <SelectEmpleadosItem
                  empresaId={this.props.empresaId}
                  headersToken={this.props.headersToken}
                  sinBorde
                  autoFocus
                  dropdownMatchSelectWidth={600}
                  value={empleadoId}
                  onChange={this.onChangeEmpleado}
                  allowClear
                  width="100%"
                  placeholder="Empleados"
                />

                {!!empleadoNombre && (
                  <div style={{ marginTop: 6, fontSize: 12, color: "#999" }}>
                    Seleccionado: {empleadoNombre}
                  </div>
                )}
              </div>

              <div>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
                  Rango de fechas (máx. 120 días)
                </div>

                <RangePicker
                  value={rango}
                  onChange={this.onChangeRango}
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  disabledDate={this.disabledDate}
                  allowClear={false}
                />

                <div style={{ fontSize: 12, color: "#999", marginTop: 8 }}>
                  Tip: si cambias el empleado o el rango, se recarga automáticamente.
                </div>
              </div>
            </Card>
          </Col>

          {/* Panel calendario */}
          <Col xs={24} md={16}>
            <Card
              title="Calendario"
              size="small"
              extra={loading ? <Tag color="processing">Cargando...</Tag> : null}
              bodyStyle={{ padding: 0 }}
              style={{
                minHeight: "80vh",
                borderRadius: 6,
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <Spin spinning={loading}>{this.renderCalendario()}</Spin>
            </Card>
          </Col>
        </Row>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  empresa: state.empresa.nombre,
});

export default connect(mapStateToProps)(CalendarioEmpleado);